/*
 * benvel-network-toast — D9 Tier 3 polite-failure toast subscriber.
 *
 * Subscribes to `benvel:network-error` (emitted by
 * assets/benvel-section-renderer-helpers.js — Story 1.6, see docs/events.md)
 * and toggles a Liquid-rendered toast into view. The toast renders ONLY the
 * static i18n string `state.network_retry`; it never DOM-renders event
 * detail (PII allowlist, project-context §63).
 *
 * Anchors: D9 (architecture.md:352, 682-712, 696), Story 1.6 emitter,
 * project-context §22-§24, §63.
 */

import { Component } from "@theme/component";

const DISMISS_AFTER_MS = 6000;

class BenvelNetworkToast extends Component {
  #dismissTimer = null;
  #announceFrame = null;
  #closeButton = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener("benvel:network-error", this.#onNetworkError, {
      passive: true,
    });
    document.addEventListener("keydown", this.#onKeydown);

    this.#closeButton = this.querySelector(".benvel-network-toast__close");
    if (this.#closeButton) {
      this.#closeButton.addEventListener("click", this.#onCloseClick);
    }

    document.dispatchEvent(
      new CustomEvent("benvel:network-error-toast-mounted", { bubbles: true }),
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("benvel:network-error", this.#onNetworkError);
    document.removeEventListener("keydown", this.#onKeydown);
    if (this.#closeButton) {
      this.#closeButton.removeEventListener("click", this.#onCloseClick);
      this.#closeButton = null;
    }
    this.#clearTimer();
    this.#clearAnnouncementFrame();
  }

  /**
   * Handle a network-error dispatch.
   *
   * The event detail (sectionId, source, error.{name,message}) is intentionally
   * NOT rendered to the DOM — toast copy is the static i18n string already
   * server-rendered by Liquid. Detail observation is reserved for analytics
   * subscribers (project-context §63 PII allowlist).
   */
  #onNetworkError = () => {
    const body = this.querySelector(".benvel-network-toast__body");
    const message = this.dataset.message || "";

    this.#clearTimer();
    this.#clearAnnouncementFrame();
    this.hidden = false;
    this.removeAttribute("aria-hidden");
    this.dataset.state = "hidden";

    if (body) {
      body.textContent = "";
      this.#announceFrame = window.requestAnimationFrame(() => {
        body.textContent = message;
        this.dataset.state = "visible";
        this.#announceFrame = null;
      });
    } else {
      this.dataset.state = "visible";
    }

    this.#dismissTimer = window.setTimeout(
      () => this.#dismiss(),
      DISMISS_AFTER_MS,
    );
  };

  #onKeydown = (event) => {
    if (event.key !== "Escape") return;
    if (this.dataset.state !== "visible") return;
    this.#dismiss();
  };

  #onCloseClick = () => {
    this.#dismiss();
  };

  #dismiss() {
    this.#clearTimer();
    this.#clearAnnouncementFrame();
    this.dataset.state = "hidden";
    this.setAttribute("aria-hidden", "true");
    this.hidden = true;
  }

  #clearTimer() {
    if (this.#dismissTimer !== null) {
      window.clearTimeout(this.#dismissTimer);
      this.#dismissTimer = null;
    }
  }

  #clearAnnouncementFrame() {
    if (this.#announceFrame !== null) {
      window.cancelAnimationFrame(this.#announceFrame);
      this.#announceFrame = null;
    }
  }
}

if (!customElements.get("benvel-network-toast")) {
  customElements.define("benvel-network-toast", BenvelNetworkToast);
}
